import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import {
  connectionRows,
  GetTeamMembersDocument,
  type TeamMemberRow,
  type TeamMembershipRole,
} from "#app/lib/data";
import { buildOrderBy } from "#app/lib/query-utils";
import type { SortingState } from "@salestrak/ui";

/**
 * A team member row plus the one thing the server does not say: whether this
 * person leads the team. Lead-ness is a property of the TEAM's `leads` list,
 * not of the user, so it is joined here rather than selected.
 */
export type TeamMember = TeamMemberRow & { role: TeamMembershipRole };

export function useTeamMembers(
  teamId: string,
  sorting: SortingState,
  options?: { skip?: boolean },
) {
  const orderBy = useMemo(() => buildOrderBy(sorting, "name"), [sorting]);

  const queryVariables = useMemo(() => ({ id: teamId, orderBy }), [teamId, orderBy]);

  const { data, loading, error, refetch } = useQuery(GetTeamMembersDocument, {
    variables: queryVariables,
    skip: options?.skip ?? false,
    fetchPolicy: "cache-and-network",
  });

  const teamData = data?.team ?? null;

  // Derived from `leads`, which the detail fragment already selects in full.
  // Selecting `leadIds` alongside it would be the same data twice, and one
  // more unbatched query.
  const leadIdSet = useMemo(
    () => new Set((teamData?.leads ?? []).map((u) => u.userId)),
    [teamData?.leads],
  );

  const teamMembers = useMemo<TeamMember[]>(
    () =>
      connectionRows(data?.teamMembers).map((u) => ({
        ...u,
        role: leadIdSet.has(u.userId) ? ("lead" as const) : ("member" as const),
      })),
    [data?.teamMembers, leadIdSet],
  );

  const teamName = teamData?.teamName ?? "Team";
  const leadCount = teamData?.leads?.length ?? 0;

  const existingUserIds = useMemo(
    () => teamMembers.map((m) => m.userId),
    [teamMembers],
  );

  return {
    teamName,
    teamMembers,
    existingUserIds,
    leadCount,
    loading,
    error,
    refetch,
  };
}
